import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Tooltip } from '@c2fo/liquidity';
import { usePageFeature } from './usePageFeature';
import type { PageFlagName } from './pageFlags';

const COMING_SOON_TOOLTIP = 'Coming soon';

export interface ComingSoonNavLinkProps extends Omit<LinkProps, 'to'> {
  /** Route path (e.g. /insights). */
  to: LinkProps['to'];
  /** Split.io flag name. When treatment is 'on', link works; otherwise disabled with "Coming soon" tooltip. */
  splitName: PageFlagName;
  /** Optional tooltip text when disabled. */
  comingSoonMessage?: string;
  /** Class name for the wrapper (e.g. for active state). */
  className?: string;
  children: React.ReactNode;
}

/**
 * Nav link that is disabled with a "Coming soon" tooltip when the page feature flag is off.
 * Uses Liquidity Tooltip; do not hide the link—show it disabled with tooltip on hover.
 */
export const ComingSoonNavLink: React.FC<ComingSoonNavLinkProps> = ({
  to,
  splitName,
  comingSoonMessage = COMING_SOON_TOOLTIP,
  className = '',
  children,
  ...linkProps
}) => {
  const { enabled } = usePageFeature(splitName);

  if (enabled) {
    return (
      <Link to={to} className={className} {...linkProps}>
        {children}
      </Link>
    );
  }

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span
            className={`cursor-not-allowed opacity-70 no-underline font-medium hover:text-gray-200 transition-colors ${className}`}
            aria-disabled="true"
            data-testid={`coming-soon-nav-${splitName}`}
          >
            {children}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content side="bottom" variant="dark">
          {comingSoonMessage}
        </Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  );
};
